// components
import { Autocomplete } from '@mui/material';
import TextField from '@mui/material/TextField';

// hooks
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceValue } from 'usehooks-ts';
import { useLatestRef } from 'components/shared/hooks/useLatestRef';

// types
import type { AutocompleteChangeReason } from '@mui/material';
import type { SearchProps } from './Search.types';

export default function Search<T>({
  debounceInMS = 500,
  disabled,
  error,
  fullWidth = false,
  helperText,
  label,
  labelProperty,
  loading,
  idProperty,
  minWidth = 220,
  multiple = false,
  noOptionsText,
  onSelected,
  onQueryChange,
  options = [],
  ref,
  showOptionsOnFocus,
  value,
  variant = 'outlined'
}: SearchProps<T>) {
  'use memo';

  const { t, i18n } = useTranslation('common');
  const [query, setQuery] = useState<string>('');
  const [debouncedQuery] = useDebounceValue(query, debounceInMS);

  const handleSelect = (
    option: T | T[] | null | undefined,
    reason: AutocompleteChangeReason
  ) => {
    onSelected?.(option as any, reason);
    setQuery('');
  };

  // Callers pass `onQueryChange` inline, so listing it as a dependency replayed the current
  // query at every parent re-render. A query is reported once, when it settles.
  const latestOnQueryChange = useLatestRef(onQueryChange);

  useEffect(() => {
    if (debouncedQuery.length > 0) {
      latestOnQueryChange.current?.(debouncedQuery);
    }
  }, [debouncedQuery, latestOnQueryChange]);

  // MUI wants a string label and the sort needs the same one; nullish labels become ''
  // so an option without a label sorts first instead of landing under "undefined".
  const getLabel = (option: T) => String(option?.[labelProperty] ?? '');

  // `toSorted` copies — `sort` would reorder the caller's own array as a side effect of
  // rendering. `localeCompare` orders by the active locale (`>` compares UTF-16 code
  // units: every capital before every lowercase, diacritics after "z") and returns 0 for
  // equal labels, which keeps the sort stable.
  const sortedOptions =
    showOptionsOnFocus || (debouncedQuery && !loading)
      ? options.toSorted((a, b) =>
          getLabel(a).localeCompare(getLabel(b), i18n.language, {
            numeric: true
          })
        )
      : [];

  return (
    <Autocomplete
      value={value}
      inputValue={query}
      onInputChange={(_, newInput) => {
        setQuery(newInput);
        if (newInput.length === 0) onSelected?.(null, 'clear');
      }}
      autoHighlight
      options={sortedOptions}
      noOptionsText={noOptionsText}
      loadingText={`${t('searching')}...`}
      loading={loading}
      multiple={multiple}
      onChange={(
        _,
        _value: T | T[] | null,
        reason: AutocompleteChangeReason
      ) => {
        const value = multiple ? (_value as T[]) : (_value as T | null);
        handleSelect(value, reason);
      }}
      onBlur={() => setQuery('')}
      ref={ref}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          size="small"
          label={label}
          error={error}
          helperText={helperText}
          fullWidth={fullWidth}
          sx={{
            minWidth,
            width: fullWidth ? '100%' : 220
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const query: string = event.target.value;
            setQuery(query);
          }}
        />
      )}
      isOptionEqualToValue={(option: T, v: T) =>
        option[idProperty] === v?.[idProperty]
      }
      disabled={disabled}
      getOptionLabel={getLabel}
    />
  );
}
